import { supabase } from './supabase'
import type { ReactionCounts, ReactionKind } from './feed'
import { PEOPLE_PAGE, type PeoplePage } from './mentions'
import { noteBalance, setStarPulseRefresher } from './starPulse'

/**
 * Star One — the two currencies, giving, and the wall. REC-6, REC-7, REC-2, REC-3.
 *
 * ── EVERY CALL IS AN RPC, FOR THE REASONS 012 AND 011 ALREADY GAVE ──────────
 *
 * A recognition is a parent row plus a locale variant plus a side table
 * (rule 6), which variant a viewer gets is app.resolve_variant()'s chain, and
 * supabase-js has no LATERAL. A client-side `.from('content')` read would
 * re-derive that chain in TypeScript. And `.insert().select()` cannot work at
 * all: RETURNING makes the statement a read and app.content_is_visible()
 * re-queries a row its own command has not committed (OPS-23).
 *
 * ── DO NOT SORT `wall` IN THIS FILE ─────────────────────────────────────────
 *
 * Newest first is the ORDER BY in recognition_wall(), and it is the product
 * rather than a default: a wall sorted by gift size is a league table of who
 * was thanked most expensively, which is the thing D10 refuses. A client-side
 * sort that disagrees with the database is invisible until somebody notices
 * the big ones float. There is deliberately no sort argument to pass.
 *
 * ── AND DO NOT FILTER IT HERE EITHER ────────────────────────────────────────
 *
 * `p_limit` is applied by the DATABASE. Narrowing a fetched page of 30 down to
 * the Safety ones returns "some of the Safety recognitions", not "the Safety
 * recognitions" — the argument written down five times now (039, 044, 046,
 * 054, 062b). The server's p_endorsements filter exists for exactly that
 * reason; the wall stopped offering it on 30 Sep, so nothing here sends it.
 *
 * ── THE TWO NUMBERS NEVER BECOME ONE ────────────────────────────────────────
 *
 * `allowanceRemaining` is what you may GIVE this month and expires; `balance`
 * is what you have BEEN GIVEN and is the only thing the shop will spend. There
 * is no `total` here and there must not be: a single figure that rises when
 * you are thanked and falls when you thank somebody is wrong in a way people
 * notice within a day, and once redemption exists it is the bug that lets a
 * jacket be bought with a giving allowance.
 *
 * ── THERE IS NO CASH FIGURE, ANYWHERE ───────────────────────────────────────
 *
 * D13. The 1c planning guide is not stored, not served and not rendered. If a
 * currency ever appears in this file, the schema invariant that forbids the
 * column will not catch it — this comment is the only guard on the client
 * side.
 */

export type MyStars = {
  periodMonth: string
  allowanceTotal: number
  allowanceSpent: number
  allowanceRemaining: number
  balanceEarned: number
  balanceSpent: number
  balanceAvailable: number
  /**
   * REC-19. Stars given to you whose fifteen-minute window has not shut, and
   * which the giver can still take back. NOT part of `balanceAvailable` and
   * must never be added to it: the whole reason the hold exists is that these
   * are not yours to spend yet.
   */
  balanceHeld: number
}

export type WallItem = {
  contentId: string
  publishedAt: string
  giverPersonId: string
  giverName: string
  personId: string
  personName: string
  /**
   * What it was for — one to three, in the giver's order, labelled in the
   * READER's language by the server (128). Canonical rows: an alias an admin
   * merged away arrives as the word it was merged into.
   */
  endorsements: Endorsement[]
  stars: number
  body: string
  variantLocale: string
  isFallback: boolean
  editedAt: string | null
  reactions: ReactionCounts
  myReaction: ReactionKind | null
  /** When the author's window shuts and the stars mature. REC-19. */
  editableUntil: string
  /**
   * The SERVER's answer to "may this reader change this row" — author, and
   * still inside the window. The client may hide a control it knows has gone
   * stale on a page left open, but it never decides: edit_recognition and
   * withdraw_recognition re-check both halves themselves.
   */
  canEdit: boolean
}

export type PersonOption = {
  personId: string
  displayName: string
  /**
   * REC-2's disambiguation, and it takes BOTH of these — measured on the live
   * roster in 062c, not reasoned. Business unit separates the two Kenneth
   * Taylors and not the two James Millers; job title separates the Millers and
   * not the Taylors. Render only one and half the duplicates become two
   * identical rows again, silently.
   */
  businessUnit: string | null
  jobTitle: string | null
}

export async function loadMyStars(): Promise<MyStars> {
  const { data, error } = await supabase.rpc('my_stars')
  if (error) throw error
  const r = (data ?? [])[0]
  // No row is impossible — my_stars() always returns one, falling back to the
  // configured allowance when the person has not given anything this month.
  // Defaulting to zeros here rather than throwing would render "0 to give" to
  // somebody with a full allowance, which is the worse failure.
  if (!r) throw new Error('my_stars returned no row')
  // Every read of the balance feeds the "+25 stars" pill — the one place the
  // comparison happens (starPulse.ts).
  noteBalance(r.balance_earned)
  return {
    periodMonth: r.period_month,
    allowanceTotal: r.allowance_total,
    allowanceSpent: r.allowance_spent,
    allowanceRemaining: r.allowance_remaining,
    balanceEarned: r.balance_earned,
    balanceSpent: r.balance_spent,
    balanceAvailable: r.balance_available,
    balanceHeld: r.balance_held ?? 0,
  }
}

setStarPulseRefresher(loadMyStars)

/**
 * ENDORSEMENTS — what a recognition is FOR (128, David 30 Sep).
 *
 * ── FREE TO TYPE, NUDGED TOWARDS WHAT EXISTS ────────────────────────────────
 *
 * Anybody may endorse for anything: an unknown word coins a COMMUNITY
 * endorsement, spelt as typed. The featured OFFICIAL ones are preset chips, and
 * as somebody types, `suggest_endorsements` offers what already exists —
 * official first, then the words people actually use — so the vocabulary
 * converges without an admin in front of it. A Star One admin curates AFTER
 * the fact (#/admin/stars/endorsements; was #/stars/admin until 9 Oct): makes a good word official and translates it,
 * features it, hides it, or merges a duplicate into it.
 *
 * ── THE SERVER IS THE ONE NORMALISER ────────────────────────────────────────
 *
 * "Client care", "client-care" and "#ClientCare" are one endorsement, and so is
 * "Atención al cliente" — because the SERVER matches on 019's normalised form
 * and on every official label in every locale. This file sends the words as
 * typed or as picked and never decides whether two of them are the same;
 * `sameWords` below only stops one chip being pressed twice.
 *
 * ── LABELS ARE DATA NOW ─────────────────────────────────────────────────────
 *
 * The category labels used to be app copy (REC-1). They moved into
 * `endorsement_label`, where an admin edits them, and the server resolves them
 * for the reader with a per-row fallback to English. There is no label map in
 * this file and there must not be one again: it would be a second source that
 * disagrees with the admin screen the first time anybody uses it.
 */
export type Endorsement = {
  id: string
  label: string
  official: boolean
}

export type EndorsementOption = {
  endorsementId: string
  label: string
  isOfficial: boolean
  isFeatured: boolean
  /** Live recognitions carrying it — a count per WORD, never per person. */
  uses: number
}

export type EndorsementPage = {
  options: EndorsementOption[]
  /** How many matched before the limit (REC-54: a page must say so). */
  total: number
}

/** 128's server rule, repeated only so the form can stop at it. */
export const MAX_ENDORSEMENTS = 3

/** The same word as far as a CHIP is concerned. Presentation only. */
export function sameWords(a: string, b: string): boolean {
  const k = (x: string) => x.trim().replace(/^#+/, '').replace(/\s+/g, ' ').toLocaleLowerCase()
  return k(a) === k(b)
}

/** The preset chips: featured official endorsements, in the admin's order. */
export async function loadFeaturedEndorsements(): Promise<Endorsement[]> {
  const { data, error } = await supabase.rpc('featured_endorsements')
  if (error) throw error
  return ((data ?? []) as Record<string, unknown>[]).map((r) => ({
    id: r.endorsement_id as string,
    label: r.label as string,
    official: true,
  }))
}

/**
 * What already exists, as somebody types. The ORDER is the server's (official
 * first, then words that start with what was typed, then use) and so is the
 * limit — do not sort or filter this page (standing rule 1).
 */
/**
 * The most `suggest_endorsements` returns in one page — 128 clamps p_limit to
 * 50. "Show all" (336) asks for this many; past it, its foot says so.
 */
export const ENDORSEMENTS_ALL = 50

export async function suggestEndorsements(query: string, limit = 8): Promise<EndorsementPage> {
  const { data, error } = await supabase.rpc('suggest_endorsements', {
    p_query: query.trim() ? query : null,
    p_limit: limit,
  })
  if (error) throw error
  const rows = (data ?? []) as Record<string, unknown>[]
  return {
    options: rows.map((r) => ({
      endorsementId: r.endorsement_id as string,
      label: r.label as string,
      isOfficial: Boolean(r.is_official),
      isFeatured: Boolean(r.is_featured),
      uses: (r.uses as number) ?? 0,
    })),
    total: rows.length ? Number(rows[0].total_matches) : 0,
  }
}

/** The jsonb the wall and the earnings return, as typed rows. */
export function endorsementsFrom(v: unknown): Endorsement[] {
  if (!Array.isArray(v)) return []
  return (v as Record<string, unknown>[]).map((e) => ({
    id: e.id as string,
    label: e.label as string,
    official: Boolean(e.official),
  }))
}

/**
 * Whose recognitions (072).
 *
 * A NARROWING TO YOURSELF AND NEVER A WAY TO READ SOMEBODY ELSE'S. The function
 * takes no person argument, so "received" can only ever mean the caller's — the
 * distinction that keeps this a filter on a list rather than the league table
 * D10 forbids and B22 declined. An unknown value RAISES server-side rather than
 * returning an empty wall, which would read as "nobody has ever thanked you".
 */
export type WallScope = 'all' | 'received' | 'given'

export async function loadWall(opts: {
  before?: string | null
  limit?: number
  scope?: WallScope
} = {}): Promise<WallItem[]> {
  const { data, error } = await supabase.rpc('recognition_wall', {
    p_limit: opts.limit ?? 30,
    p_before: opts.before ?? null,
    // null and [] are treated identically by the function, so "cleared" and
    // "never set" cannot become two states with one appearance (046).
    p_scope: opts.scope ?? 'all',
  })
  if (error) throw error
  return (data ?? []).map(toWallItem)
}

/** One recognition_wall row into the card's shape. Shared with the feed's
 *  groups (333), which return the wall's own columns for the same reason:
 *  one card, one shape, one mapping. */
export function toWallItem(r: Record<string, unknown>): WallItem {
  return {
    contentId: r.content_id as string,
    publishedAt: r.published_at as string,
    giverPersonId: r.giver_person_id as string,
    giverName: (r.giver_name as string) ?? 'Someone',
    personId: r.person_id as string,
    personName: (r.person_name as string) ?? 'Someone',
    endorsements: endorsementsFrom(r.endorsements),
    stars: (r.stars as number) ?? 0,
    body: (r.body as string) ?? '',
    variantLocale: r.variant_locale as string,
    isFallback: Boolean(r.is_fallback),
    editedAt: (r.edited_at as string) ?? null,
    reactions: (r.reactions ?? {}) as ReactionCounts,
    myReaction: (r.my_reaction as ReactionKind) ?? null,
    editableUntil: r.editable_until as string,
    canEdit: Boolean(r.can_edit),
  }
}

/**
 * RECOGNITIONS IN THE FEED — 333 (30 Oct 2026; a platform-support idea, David's
 * shape: one grouped card between posts).
 *
 * `bounds` are the publish times of the posts on the page, newest first (see
 * `placeRecognitionGroups` in lib/feed.ts). Gap 0 sits above the newest post,
 * gap g between post g-1 and post g. Per gap the DATABASE returns the newest
 * few and `total`, the number in the gap — so "Show all 6" is never a count of
 * a fetched page (standing rule 1). Order is the wall's: newest first, never by
 * stars (D10).
 */
export type RecognitionGroup = {
  gap: number
  /** Every visible recognition in the gap; at 100 the server says `capped`. */
  total: number
  capped: boolean
  /** The newest few, newest first. */
  items: WallItem[]
  /** The gap's own window, for Show all: [lower, upper). upper null = now. */
  lower: string
  upper: string | null
}

export const FEED_RECOGNITIONS_PER_GROUP = 3

export async function loadFeedRecognitions(bounds: string[]): Promise<Map<number, RecognitionGroup>> {
  const out = new Map<number, RecognitionGroup>()
  if (bounds.length === 0) return out
  const { data, error } = await supabase.rpc('feed_recognition_groups', {
    p_bounds: bounds,
    p_per_group: FEED_RECOGNITIONS_PER_GROUP,
  })
  if (error) throw error
  for (const r of (data ?? []) as Record<string, unknown>[]) {
    const gap = r.gap as number
    let g = out.get(gap)
    if (!g) {
      g = {
        gap,
        total: (r.gap_total as number) ?? 0,
        capped: Boolean(r.capped),
        items: [],
        lower: bounds[gap],
        upper: gap === 0 ? null : bounds[gap - 1],
      }
      out.set(gap, g)
    }
    g.items.push(toWallItem(r))
  }
  return out
}

/**
 * Every recognition in one group, for Show all. The limit IS the group's own
 * total from the database (at most 100, the wall's cap), and the wall's newest
 * rows before the gap's upper bound are the gap's rows — so the filter below
 * only ever drops something that changed between the two reads (a recognition
 * hidden meanwhile lets an older one in). It is not narrowing a page.
 */
export async function loadRecognitionGroup(g: RecognitionGroup): Promise<WallItem[]> {
  const rows = await loadWall({ before: g.upper, limit: Math.min(Math.max(g.total, 1), 100) })
  const lower = new Date(g.lower).getTime()
  return rows.filter((w) => new Date(w.publishedAt).getTime() >= lower)
}

/**
 * THE STARS YOU EARNED — 106, REC-57 (David, 24 Sep).
 *
 * ── EVERYTHING THAT RAISES THE BALANCE, AND THE PROOF IS A SUM ──────────────
 *
 * Recognitions received AND activity (099), including the 1,000 on first
 * sign-in — David's call, "everything, everywhere", taken knowing month one is
 * one tall bar for everybody. `app.star_credits()` is the one definition, and
 * summed over all time it EQUALS `balanceEarned` (asserted server-side). So
 * the chart under the number adds up to the number. It used to be 073's
 * `my_star_history()`, recognitions only — which drew 50 stars of bars under
 * a balance of 1,065, with nothing on screen explaining the gap. 114 dropped
 * it (25 Sep); do not bring it back.
 *
 * ── SELF-ONLY, AND THE SIGNATURE IS THE GUARD (D10, B22) ────────────────────
 *
 * Neither call takes a person. A per-person earnings history handed to anybody
 * but its owner is performance data about how VISIBLE somebody is, and ~40% of
 * this company is not in front of a screen. If a `p_person` ever appears on
 * either call, the leaderboard is back.
 *
 * ── BUCKETED ON THE CREDIT ──────────────────────────────────────────────────
 *
 * A recognition lands when its fifteen-minute hold ends (063), not when it was
 * given, so no closed bucket grows later. Days and weeks are the person's own
 * timezone (rule 9); months are the UTC star month, the same month the
 * allowance card above it counts in (073).
 */
export type StarRange = '1m' | '3m' | '12m' | 'all'
export const STAR_RANGES: readonly StarRange[] = ['1m', '3m', '12m', 'all']

export type StarBucket = {
  /** First day of the bucket, date-only ISO (`2026-09-01`). */
  start: string
  stars: number
  credits: number
  granularity: 'day' | 'week' | 'month'
}

export type StarCredit = {
  creditId: string
  creditedAt: string
  source: 'recognition' | 'activity'
  /** Activity only — a key of app.star_earning_catalogue(). */
  ruleKey: string | null
  /** Recognition only: what it was for, in the reader's language (128). */
  endorsements: Endorsement[]
  /** NET: what is in the balance now. */
  stars: number
  /** Taken back when the thing that earned it was removed (099). */
  reversedStars: number
  /** Recognition only. */
  giverName: string | null
}

export type StarCreditPage = {
  credits: StarCredit[]
  /** Every credit you have — not the rest of this page (standing rule 1). */
  total: number
}

/**
 * The zero-fill is `generate_series` in the DATABASE, so the browser is not the
 * thing that remembers how many bars a range has. A range whose value is not
 * one of the four RAISES server-side rather than drawing an empty chart.
 */
export async function loadStarSeries(range: StarRange): Promise<StarBucket[]> {
  const { data, error } = await supabase.rpc('my_star_series', { p_range: range })
  if (error) throw error
  return (data ?? []).map((r: Record<string, unknown>) => ({
    start: r.bucket_start as string,
    stars: (r.stars as number) ?? 0,
    credits: (r.credits as number) ?? 0,
    granularity: r.granularity as StarBucket['granularity'],
  }))
}

/**
 * Newest first, keyset-paged on (credited_at, credit_id). Pass the LAST row of
 * the previous page as `after`. Do not sort or filter the result here — the
 * page is the database's, and narrowing it in the browser returns "some of
 * them" (standing rule 1).
 */
export async function loadStarEarnings(opts: {
  limit?: number
  after?: Pick<StarCredit, 'creditedAt' | 'creditId'> | null
} = {}): Promise<StarCreditPage> {
  const { data, error } = await supabase.rpc('my_star_earnings', {
    p_limit: opts.limit ?? 20,
    p_before_at: opts.after?.creditedAt ?? null,
    p_before_id: opts.after?.creditId ?? null,
  })
  if (error) throw error
  const rows = (data ?? []) as Record<string, unknown>[]
  return {
    credits: rows.map((r) => ({
      creditId: r.credit_id as string,
      creditedAt: r.credited_at as string,
      source: r.source as StarCredit['source'],
      ruleKey: (r.rule_key as string) ?? null,
      endorsements: endorsementsFrom(r.endorsements),
      stars: (r.stars as number) ?? 0,
      reversedStars: (r.reversed_stars as number) ?? 0,
      giverName: (r.giver_name as string) ?? null,
    })),
    // An empty page carries no row to read the count from; that is zero
    // credits only when there was no cursor. Past the end it is "unknown",
    // and the caller already holds the count from the first page.
    total: rows.length ? ((rows[0].total_count as number) ?? 0) : 0,
  }
}

/**
 * REC-19. Both of these are REPLACE calls with no defaults — 049's rule, and
 * the reason is that with defaults "clear the endorsements" and "leave them
 * alone" become the same call and the silent outcome is data loss. 128: the
 * whole set of endorsements goes every time, and an empty set is REFUSED by
 * the server rather than read as "keep what it had".
 *
 * `p_category` is sent as NULL on purpose: it is the pre-128 call shape the
 * server keeps for positional callers, and the web never uses it.
 *
 * THERE IS NO RECIPIENT ARGUMENT AND THERE MUST NOT BE ONE. Changing who you
 * thanked is a different recognition, and the stars are already sitting in the
 * first person's held figure.
 */
export async function editRecognition(input: {
  contentId: string
  /** The words, as picked or typed. */
  endorsements: string[]
  body: string
  stars: number
}): Promise<void> {
  const { error } = await supabase.rpc('edit_recognition', {
    p_content_id: input.contentId,
    p_category: null,
    p_body: input.body,
    p_stars: input.stars,
    p_endorsements: input.endorsements,
  })
  if (error) throw error
}

export async function withdrawRecognition(contentId: string, reason?: string): Promise<string> {
  const { data, error } = await supabase.rpc('withdraw_recognition', {
    p_content_id: contentId,
    p_reason: reason ?? null,
  })
  if (error) throw error
  return data as string
}

/**
 * How long the window was, in whole minutes, read from the ROW rather than
 * from a constant here. The policy is a column (`star_policy.edit_window`) so
 * that changing fifteen minutes to ten is an UPDATE and not a release — and a
 * hard-coded "15" in this file would quietly survive that change and start
 * lying to people about how long they have.
 */
export function editWindowMinutes(item: Pick<WallItem, 'publishedAt' | 'editableUntil'>): number {
  const ms = new Date(item.editableUntil).getTime() - new Date(item.publishedAt).getTime()
  return Math.max(1, Math.round(ms / 60_000))
}

/**
 * Whether the window is still open according to the browser's clock.
 *
 * This is presentation only: it stops a page left open for an hour from
 * offering a button that will fail. `canEdit` from the server is the answer
 * that counts, and the functions re-check it regardless of what is rendered.
 */
export function stillEditable(item: WallItem, now = Date.now()): boolean {
  return item.canEdit && new Date(item.editableUntil).getTime() > now
}

/**
 * The person search for the lists that show unit and title (335).
 *
 * `find_colleagues` matches every typed word against the name, the business
 * unit or the job title — what each row shows — so "toronto driller" narrows.
 * It shares one body with the @ menu's `mentionable_people`
 * (`app.person_search`), which still matches the name only, because the @
 * menu shows only names. Callers: PersonField (recognition, the support
 * hand-off, photo reviews, surveys) and the stock room's manager picker.
 *
 * `excludeSelf` IS SENT TO THE SERVER AND IS NOT APPLIED HERE (REC-53, 087).
 * The limit belongs to the database, so dropping the caller from a fetched page
 * of eight returns seven and loses a genuine eighth match with nothing on
 * screen saying a row was removed — standing rule 1, and the exception it
 * allows (a read with no limit may be filtered client-side) is the opposite of
 * this read, which is nothing but a limit.
 *
 * It defaults to FALSE, because this is shared with the @ menu (059) and the
 * stock room's manager picker, and neither of those has a reason to hide you
 * from yourself. Only the recognition picker asks, because `give_recognition()`
 * refuses self-recognition and a control offering a write the server will
 * refuse reads as breakage rather than as policy.
 *
 * Options rather than a second positional argument, for the same reason 071
 * made every SQL call go by name: a signature that can grow will.
 */
export async function findPeople(
  query: string,
  opts: { limit?: number; excludeSelf?: boolean } = {},
): Promise<PeoplePage<PersonOption>> {
  // ONE definition of who is findable, shared with the @ menu (059) through
  // app.person_search; this door also matches unit and title (335).
  const { data, error } = await supabase.rpc('find_colleagues', {
    p_query: query,
    p_limit: opts.limit ?? PEOPLE_PAGE,
    p_exclude_self: opts.excludeSelf ?? false,
  })
  if (error) throw error
  const rows = (data ?? []) as Record<string, unknown>[]
  return {
    people: rows.map((r) => ({
      personId: r.person_id as string,
      displayName: r.display_name as string,
      businessUnit: (r.business_unit as string) ?? null,
      jobTitle: (r.job_title as string) ?? null,
    })),
    // REC-54: how many matched before the limit, from the server. Eight rows
    // out of sixteen matches is the page that started this; the caller has to
    // be able to say so. Read off the first row — 088 asserts every row of one
    // call carries the same number.
    total: rows.length ? Number(rows[0].total_matches) : 0,
  }
}

export async function giveRecognition(input: {
  personId: string
  /** The words, as picked or typed. One to three (the server enforces it). */
  endorsements: string[]
  body: string
  stars: number
}): Promise<string> {
  const { data, error } = await supabase.rpc('give_recognition', {
    p_recognises_person_id: input.personId,
    p_body: input.body,
    p_stars: input.stars,
    p_endorsements: input.endorsements,
  })
  if (error) throw error
  return data as string
}

/**
 * The preset amounts. The mockup's argument, kept because it is the reason
 * they exist: an empty box makes 933 people guess and the guesses will not
 * agree, so the presets teach the norm. "None" is a first-class option rather
 * than an absence — a shout-out is a recognition, not a failure to give one.
 */
export const STAR_PRESETS = [0, 25, 50, 100, 250] as const

/**
 * REC-17. A typed amount, brought into range.
 *
 * ── THE CAP IS THE ALLOWANCE, NEVER THE BALANCE ─────────────────────────────
 *
 * David's words were "custom amount up to balance", and taking that literally
 * would undo the whole two-currency design: your BALANCE is what you have been
 * given and is the only thing the shop will ever spend, so capping a gift at it
 * lets somebody give away stars they received. That is the collapse 061 built
 * two tables to prevent, and the plan calls it the mistake that gets expensive
 * the day redemption exists. What you may give is `allowanceRemaining`.
 *
 * This is a UI cap over an ENFORCED one: `star_period`'s CHECK makes an overdraw
 * impossible whatever the client sends, so nothing here is a boundary. It exists
 * so the person finds out before pressing the button rather than after.
 *
 * Empty, blank and junk all come back as 0 rather than NaN — a NaN reaches the
 * disabled check as `false` for every comparison and silently enables a submit
 * that the server then refuses.
 */
export function clampGift(raw: string | number, allowanceRemaining: number): number {
  const n = typeof raw === 'number' ? raw : Number.parseInt(raw.trim(), 10)
  if (!Number.isFinite(n) || n <= 0) return 0
  return Math.min(Math.floor(n), Math.max(0, allowanceRemaining))
}

/** Days left in the current UTC month, for "resets in N days". */
export function daysLeftInPeriod(now = new Date()): number {
  const end = Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1)
  return Math.max(0, Math.ceil((end - now.getTime()) / 86_400_000))
}
